/**
 * [PURPOSE]: Migration chain for character-data schema versions.
 * [WHY]: Different app versions write data with different schemas; on
 *   load, we step through the MIGRATIONS map in order, transforming
 *   the data to the CURRENT_SCHEMA_VERSION. Without this, old saves
 *   would either crash or silently lose fields.
 * [INTEGRATION]: Called by loadCharacter (in S1) before any consumer
 *   reads character data. Each MIGRATIONS[N] entry transforms data
 *   from version N to version N+1.
 * [SPEC]: docs/superpowers/specs/2026-04-28-d1-v1-migration-design.md
 *   — section "Versioning Scheme" + "Pre-v1 migration heuristic"
 *
 * In D1: scaffold only. Migration logic lives here as no-ops.
 * In S1: migrateFromPreV1 (key 0 → 1) is implemented.
 */

import { CURRENT_SCHEMA_VERSION } from './schemaVersion';

/**
 * Map of version-from-N to transformer function.
 * Each transformer accepts data with schemaVersion N and returns data
 * with schemaVersion N+1.
 *
 * [B16 Task 4]: MIGRATIONS[0] is intentionally minimal — it stamps
 * schemaVersion only. v1 has not released; there are no users with
 * pre-v1 saves to protect via data fixups. Inline canonical-paths
 * fixes in loadCharacter (App.jsx) cover the user's own internal test
 * data continuity. Future MIGRATIONS[1+] plug in here when v1.x
 * systems land — see the D2 deferred-system migration plans doc for
 * per-system commitments.
 */
function migrateFromPreV1(data) {
  return { ...data, schemaVersion: 1 };
}

/**
 * v1 → v2 (Spec A — Wizard Capstones / Generic Feature Mechanics).
 *
 * [PURPOSE]: Move Wizard-specific capstone paths into the generic
 *   customClassPicks bucket so a single map serves SRD Wizard AND
 *   CW custom-class player-picked spells.
 * [WHY]: Spec A Pillar 2 — generic state replaces N class-specific
 *   paths. Pre-migration shape (legacy reference — these paths are
 *   intentionally NOT used by post-Spec-A code):
 *     playerStats.spellMastery   = { firstLevel, secondLevel }     // lint-exempt: doc-of-legacy
 *     playerStats.signatureSpells = ['SpellA', 'SpellB']           // lint-exempt: doc-of-legacy
 *   Post-migration shape:
 *     playerStats.customClassPicks = {
 *       Wizard: {
 *         spell_mastery:    ['SpellA', 'SpellB'],
 *         signature_spells: ['SpellA', 'SpellB'],
 *       }
 *     }
 * [SPEC]: docs/superpowers/specs/2026-05-16-wizard-capstones-and-generic-feature-mechanics-design.md
 *
 * Idempotent: if playerStats.spellMastery / signatureSpells are absent,
 * the function only stamps schemaVersion=2 + leaves customClassPicks alone.
 *
 * Per-rest spell usage (signatureSpellsUsed) was NOT a production field —
 * Wizard signature spells had no use-tracking pre-migration. The new
 * effect-type pipeline routes consumption through the existing
 * playerStats._perRestSpellUses map (consumePerRestSpellUse helper) so
 * no migration is needed for tracking state.
 */
function migrateV1ToV2(data) {
  if (!data || !data.playerStats) {
    return { ...data, schemaVersion: 2 };
  }
  const ps = data.playerStats;
  const customClassPicks = { ...(ps.customClassPicks || {}) };
  const wizardPicks = { ...(customClassPicks.Wizard || {}) };

  if (ps.spellMastery && typeof ps.spellMastery === 'object') {
    const picks = [ps.spellMastery.firstLevel, ps.spellMastery.secondLevel].filter(Boolean);
    if (picks.length > 0) wizardPicks.spell_mastery = picks;
  }

  if (Array.isArray(ps.signatureSpells) && ps.signatureSpells.length > 0) {
    wizardPicks.signature_spells = [...ps.signatureSpells];
  }

  if (Object.keys(wizardPicks).length > 0) customClassPicks.Wizard = wizardPicks;

  const { spellMastery, signatureSpells, ...restPs } = ps;
  // Only stamp customClassPicks when there's actual content OR the source
  // already had it. Avoids polluting fresh v1 saves with empty objects.
  const nextPs = Object.keys(customClassPicks).length > 0
    ? { ...restPs, customClassPicks }
    : restPs;
  return {
    ...data,
    schemaVersion: 2,
    playerStats: nextPs,
  };
}

/**
 * v2 → v3 (Spec B — Feature Action Surfaces).
 *
 * [PURPOSE]: Stamp new generic state fields used by Spec B's three new
 *   action-surface effect types (spendActionMenu / reactionTrigger /
 *   longRestPicker):
 *     - playerStats.classKnownSelections: Record<className, Record<pickerId, string[]>>
 *     - playerStats.classActiveSelections: Record<className, Record<pickerId, ActiveSelection[]>>
 *   The fields default to empty objects when not already present. No
 *   existing data shape migrates — these are NEW writes populated as
 *   players use the mechanics (Artificer Infuse Item recipe picks at
 *   level-up, Spell-Storing Item daily binding, etc.).
 * [WHY]: Spec B Pillar 2 — generic state. Subsequent reads (RestPanel
 *   DailySetupModal, LevelUpWizard infusion picker, ClassResourcesPanel
 *   Spend Options pane) all assume these fields exist as objects.
 * [SPEC]: docs/superpowers/specs/2026-05-16-feature-action-surfaces-design.md
 *
 * Rule 18 safety: the reserved-fields registry includes `reactionTriggers`
 * (plural) for v1.1 Combat-Reaction Triggers. Spec B's new singular
 * `reactionTrigger` effect type does NOT collide — its registrations live
 * in effectRegistry; invoke-time state lives in classActiveSelections;
 * pool decrements via classResources. No write at playerStats.reactionTriggers.
 */
function migrateV2ToV3(data) {
  if (!data || !data.playerStats) {
    return { ...data, schemaVersion: 3 };
  }
  const ps = data.playerStats;
  return {
    ...data,
    schemaVersion: 3,
    playerStats: {
      ...ps,
      classKnownSelections: ps.classKnownSelections || {},
      classActiveSelections: ps.classActiveSelections || {},
    },
  };
}

/**
 * [PURPOSE]: Stamp three new top-level state slots used by Spec D's
 *   Combat Manager + Monster Editor + Image System megaspec:
 *     - playerStats.activeEncounter: Object — current combat encounter
 *       state (combatants, initiative order, round number, grid config,
 *       canvas state). Explicitly NOT `combatState` — that path is Rule 18
 *       reserved for a different v1.1 system.
 *     - playerStats.monsterCatalog: Record<monsterId, MonsterBlock> — the
 *       user's catalog of CW-authored custom monsters. SRD monsters live
 *       in BASE_MONSTERS (data file); this slot holds homebrew.
 *     - playerStats.imageLibrary: { images: Array<ImageRef> } — index of
 *       locally-stored portraits + maps (Capacitor filesystem on mobile,
 *       IndexedDB on web). Each entry: { id, kind, name, storagePath,
 *       tags[] }. Image bytes live in storage; this is just the index.
 *
 *   All three default to empty initials when not already present.
 *
 * [WHY]: Spec D — Combat Manager megaspec lands these as new top-level
 *   slots so the rest of the spec's tasks can read/write them without
 *   schema gymnastics. Empty initials mean v3 saves load cleanly; populating
 *   happens as the player uses the Combat Manager.
 *
 * [SPEC]: docs/superpowers/specs/2026-05-17-combat-manager-design.md
 * [PLAN]: docs/superpowers/plans/2026-05-17-combat-manager.md Task 1
 *
 * Rule 18 safety: `combatState` (singular) remains reserved per
 * src/services/reservedFields.js. We deliberately use the longer-form
 * `activeEncounter` field name to sidestep the gate without policy
 * change.
 */
function migrateV3ToV4(data) {
  if (!data || !data.playerStats) {
    return { ...data, schemaVersion: 4 };
  }
  const ps = data.playerStats;
  return {
    ...data,
    schemaVersion: 4,
    playerStats: {
      ...ps,
      activeEncounter: ps.activeEncounter || emptyActiveEncounter(),
      monsterCatalog: ps.monsterCatalog || {},
      imageLibrary: ps.imageLibrary || { images: [] },
    },
  };
}

/**
 * Empty `activeEncounter` initial. No combat in progress, sensible
 * grid defaults. The shape matches what Combat Manager Phase 4-5
 * tasks will read/write.
 */
function emptyActiveEncounter() {
  return {
    active: false,
    roundNumber: 0,
    activeIndex: 0,
    combatants: [],
    gridEnabled: false,
    snapToGrid: false,
    gridSize: 40,
    canvasSize: { w: 800, h: 600 },
    mapBackdropId: null,
  };
}

/**
 * v4 → v5 (Audit #99 — Retire character.imageGallery)
 *
 * [PURPOSE]: Walk every entry in legacy `playerStats.imageGallery` →
 *   convert to `playerStats.imageLibrary.images` entry (Spec D Task 25
 *   shared store, Stone 7 expanded). Drop the legacy field afterward.
 *
 * [WHY]: Audit B6 found three image-store debt accretion: basics.portrait
 *   (active per-character) + imageGallery (legacy per-character list) +
 *   imageLibrary (Spec D shared, kind+entity tagged). The first commit
 *   in this pair (#100) switched the AI generator save callbacks from
 *   imageGallery → imageLibrary. This migration cleans up the residual
 *   data so future schemas don't need to keep reading two stores.
 *
 * [MAPPING]: Each legacy entry { id, mode?, data, name?, label?,
 *   createdAt?, ... } becomes:
 *     {
 *       id: entry.id || generated,
 *       kind: 'item' (when mode='icon') | 'portrait' (default),
 *       name: entry.name || entry.label || 'Migrated Image',
 *       data: entry.data,
 *       tags: [...(entry.tags || []), 'migrated-from-imageGallery'],
 *       addedAt: entry.createdAt timestamp || now,
 *       + entityId from basics.id / basics.name when available
 *       + entityKind: 'character' (the legacy field was per-character)
 *     }
 *
 * Idempotent: if `imageGallery` is missing or empty, this only stamps
 * schemaVersion=5 and removes the empty field if present.
 *
 * [AUDIT REF]: docs/superpowers/findings/2026-05-17-compliance-audit/B6-portrait-contract.md
 * [PAIR]: Audit #100 commit (CharacterIdentityHeader + EditItemDialog
 *   write-callback switch) lands the runtime half; this migration cleans
 *   the residual store. Together they close the dual-gallery debt.
 */
function migrateV4ToV5(data) {
  if (!data || !data.playerStats) {
    return { ...data, schemaVersion: 5 };
  }
  const ps = data.playerStats;
  // Pull off the legacy field; the result must NEVER include it.
  const { imageGallery, ...restPs } = ps;

  if (!Array.isArray(imageGallery) || imageGallery.length === 0) {
    return { ...data, schemaVersion: 5, playerStats: restPs };
  }

  const existingLibrary = ps.imageLibrary?.images || [];
  const characterId = ps.basics?.id || ps.basics?.name || '';
  const seen = new Set(existingLibrary.map((g) => g.id));
  const migrated = [];
  for (const entry of imageGallery) {
    if (!entry || typeof entry !== 'object') continue;
    if (typeof entry.data !== 'string' || entry.data.length === 0) continue;
    // Re-key id only on collision with existing library — preserves the
    // original id when possible so downstream references survive.
    let id = entry.id || `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    while (seen.has(id)) {
      id = `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    }
    seen.add(id);
    const ref = {
      id,
      kind: entry.kind === 'map' ? 'map'
        : entry.kind === 'token' ? 'token'
        : entry.kind === 'item' ? 'item'
        : entry.kind === 'entity' ? 'entity'
        : entry.mode === 'icon' ? 'item'
        : 'portrait',
      name: String(entry.name || entry.label || 'Migrated Image').slice(0, 80),
      data: entry.data,
      tags: [
        ...(Array.isArray(entry.tags) ? entry.tags : []),
        'migrated-from-imageGallery',
      ],
      addedAt: entry.createdAt
        ? (Number.isFinite(new Date(entry.createdAt).getTime())
            ? new Date(entry.createdAt).getTime()
            : Date.now())
        : Date.now(),
    };
    if (characterId) {
      ref.entityId = String(characterId);
      ref.entityKind = 'character';
    }
    migrated.push(ref);
  }

  return {
    ...data,
    schemaVersion: 5,
    playerStats: {
      ...restPs,
      imageLibrary: {
        ...(ps.imageLibrary || {}),
        images: [...existingLibrary, ...migrated],
      },
    },
  };
}

export const MIGRATIONS = {
  0: migrateFromPreV1,
  1: migrateV1ToV2,
  2: migrateV2ToV3,
  3: migrateV3ToV4,
  4: migrateV4ToV5,
};

/**
 * Keys produced by buildCurrentCharacterData. Keep in sync with App.jsx builder.
 * Last verified: 2026-05-10.
 *
 * This set and buildCurrentCharacterData() in App.jsx are PARITY PARTNERS — they
 * must stay in sync. Drift in either direction creates subtle data-loss bugs:
 *
 *   - A key present here but absent from buildCurrentCharacterData() creates a
 *     load-but-don't-save asymmetry: the field is preserved on first load, then
 *     silently dropped the next time the character is saved.
 *
 *   - A key present in buildCurrentCharacterData() but absent here causes that
 *     key to be incorrectly extracted into _unknownFields on load, which means
 *     v1 consumers will read stale/empty values instead of the saved data.
 *
 * Keys NOT in this set are captured into _unknownFields on load and spread back
 * to top level by buildCurrentCharacterData's `...unknownFields` tail, giving
 * forward-compat round-trip preservation for v1.x data without explicit listing.
 *
 * Parity partner: buildCurrentCharacterData() at src/App.jsx (~line 8479).
 */
const KNOWN_TOP_LEVEL_KEYS = new Set([
  // Added by migration chain (not by builder directly)
  'schemaVersion',
  // The _unknownFields bucket itself must stay at top level (not nested)
  '_unknownFields',
  // Keys written by buildCurrentCharacterData (verified 2026-05-10)
  'playerStats',
  'inventory',
  'vaults',
  'currencies',
  'settings',
  'knownSpells',
  'preparedSpells',
  'spellSlots',
  'customSpells',
  'manualSpellSlots',
  'activeQuickBonuses',
  'temporaryStats',
  'timeSystem',
  'currentGameDay',
  'calendarSettings',
  'skillProficiencies',
  'customShopTypes',
  'customRollTables',
  'activeShops',
  'activityLog',
  'journalEntries',
  'journalRollTables',
  'rollTables',
  'craftingProjects',
  'dmEvents',
  // Removed (CLR-005 2026-05-10): spells, savedCharacters, partyData,
  // sourcesEnabled, characterId, activeActivities, rollHistory — none of
  // these are written by buildCurrentCharacterData. Removing them means
  // any saved data with these keys is captured into _unknownFields and
  // round-trips safely via the ...unknownFields spread, rather than being
  // silently dropped after the first v1 save.
]);

/**
 * Step a character-data object through the MIGRATIONS chain
 * until it reaches CURRENT_SCHEMA_VERSION.
 *
 * Safe on null/undefined/{} — returns the input unchanged when
 * there's nothing to migrate.
 *
 * Infinite-loop guard (Q13 sweep, 2026-05-16): each migration MUST bump
 * the returned schemaVersion above the input version. If a future
 * MIGRATIONS[N] is authored without that bump (e.g. forgot the field
 * in the returned spread), the chain would otherwise re-iterate the
 * same migration forever. The guard throws with a descriptive message
 * naming the stuck index so the bug surfaces in CI/dev immediately.
 *
 * @param {Object|null|undefined} data Character data (any version).
 * @returns {Object|null|undefined} Same shape, schemaVersion ≥ CURRENT_SCHEMA_VERSION.
 */
export function migrate(data) {
  if (data == null) return data;
  if (typeof data !== 'object') return data;

  let current = data.schemaVersion ?? 0;
  let transformed = data;

  // Comfortable above any realistic chain depth (we have 1 migration today;
  // the registry is small enough that 64 leaves headroom for a decade of
  // schema evolution). Below an actual hang threshold so a stuck loop
  // surfaces as a thrown error, not a frozen tab.
  const MAX_MIGRATION_STEPS = 64;
  let steps = 0;

  while (MIGRATIONS[current]) {
    const before = current;
    transformed = MIGRATIONS[current](transformed);
    current = transformed.schemaVersion ?? current;
    steps += 1;

    if (current <= before) {
      throw new Error(
        `[characterMigrations] MIGRATIONS[${before}] did not advance ` +
        `schemaVersion (returned ${current}). Each migration must bump ` +
        `the returned schemaVersion above its input version. This is a ` +
        `bug in the migration definition — fix MIGRATIONS[${before}] to ` +
        `return { ...data, schemaVersion: ${before + 1} } (or higher).`
      );
    }
    if (steps > MAX_MIGRATION_STEPS) {
      throw new Error(
        `[characterMigrations] Migration chain exceeded ` +
        `${MAX_MIGRATION_STEPS} steps (currently at version ${current}). ` +
        `Either MIGRATIONS contains a cycle or the chain depth is ` +
        `unreasonably long. Inspect MIGRATIONS in ` +
        `src/services/characterMigrations.js.`
      );
    }
  }

  // [B16]: Extract unknown top-level keys into _unknownFields so they
  // round-trip through v1 saves without loss. Known keys are read by v1
  // consumers; unknown keys would otherwise be silently dropped on save.
  // _unknownFields lives as a sibling to playerStats/inventory at the data
  // top level, NOT nested inside playerStats — this keeps playerStats clean
  // and migration metadata separate from character data.
  // Spec: docs/superpowers/specs/2026-04-28-r1-release-readiness-design.md §B16.

  const unknownFields = {};
  let hasUnknown = false;
  for (const key of Object.keys(transformed)) {
    if (!KNOWN_TOP_LEVEL_KEYS.has(key)) {
      unknownFields[key] = transformed[key];
      hasUnknown = true;
    }
  }
  if (hasUnknown) {
    const cleanedTransformed = { ...transformed };
    for (const key of Object.keys(unknownFields)) {
      delete cleanedTransformed[key];
    }
    cleanedTransformed._unknownFields = unknownFields;
    transformed = cleanedTransformed;
  }

  return transformed;
}
